/**
 * reintentoDeRed — reintento paciente SÓLO para fallos de red.
 *
 * ── EL CASO QUE LO PRODUCE, medido el 16-sep-2026 ────────────────────────────
 * El servidor que sirve `GET /api/organizations` corre en Cloud Run con
 * `minScale = maxScale = 1`: UNA instancia como máximo. Cuando la plataforma la
 * reemplaza (4 arranques entre las 20:53 y las 20:58 de ese día) hay una ventana
 * SIN ninguna instancia capaz de servir, y Cloud Run rechaza arriba, antes del
 * contenedor. Esa respuesta no lleva los headers CORS de la app —los pone
 * `server/src/app.js:23-36`, que ni siquiera llega a ejecutarse—, así que el
 * navegador la reporta como:
 *
 *   Response to preflight request doesn't pass access control check:
 *   No 'Access-Control-Allow-Origin' header  →  net::ERR_FAILED  →  Failed to fetch
 *
 * Y NO aparece en los request logs de Cloud Run: la ausencia de log es la prueba
 * de que la respuesta se generó antes del contenedor, no de que no hubo petición.
 *
 * El presupuesto anterior eran dos esperas de 300 y 800 ms — tres intentos en poco
 * más de UN segundo. El arranque en frío medido va de 1,5 a 8,2 s, así que los tres
 * se consumían antes de que existiera nadie a quien preguntar, y el cliente veía la
 * pantalla de «no pudimos cargar tu cuenta» con el servidor perfectamente sano.
 *
 * ── POR QUÉ SÓLO LOS FALLOS DE RED ──────────────────────────────────────────
 * Un 401 o un 403 son respuestas del servidor: reintentarlos cinco veces retrasa
 * diez segundos un veredicto que ya está dado, y en el caso de un token inválido
 * multiplica por cinco las llamadas. Un `Failed to fetch` es lo contrario: nadie
 * respondió, y esperar es exactamente lo que hay que hacer.
 *
 * `fetch` lanza `TypeError` cuando la petición no llega a completarse; los errores
 * HTTP los construye `handleResponse` (`src/services/api.js`), que desde este mismo
 * cambio marca `err.status`. Un error CON `status` es del servidor y no se reintenta,
 * aunque su mensaje se parezca a uno de red.
 */

/** Esperas entre intentos: 5 intentos, ~10 s de presupuesto total. */
const ESPERAS_POR_DEFECTO = [500, 1500, 3000, 5000];

/**
 * ¿Es un fallo de RED (nadie respondió) y no una respuesta de error del servidor?
 * @param {Error} err
 * @returns {boolean}
 */
function esFalloDeRed(err) {
  if (!err) return false;
  // Un error con `status` vino de una respuesta HTTP: el servidor SÍ contestó.
  if (typeof err.status === 'number') return false;
  if (err.name === 'TypeError') return true;
  return /failed to fetch|networkerror|network request failed|load failed|err_failed/i
    .test(err.message || '');
}

/**
 * Ejecuta `fn` reintentando SÓLO si falla por red.
 *
 * @param {Function} fn                 la operación (normalmente una llamada a la API)
 * @param {object}   [opciones]
 * @param {number[]} [opciones.esperas] ms entre intentos; su longitud + 1 son los intentos
 * @param {Function} [opciones.alReintentar] (intento, total, err) — para registrar
 * @param {Function} [opciones.dormir]  inyectable en tests; por defecto setTimeout
 * @returns {Promise<*>} lo que devuelva `fn`
 * @throws el último error, si se agota el presupuesto o si NO es de red
 */
async function conReintentoDeRed(fn, opciones = {}) {
  const esperas = opciones.esperas || ESPERAS_POR_DEFECTO;
  const dormir = opciones.dormir || ((ms) => new Promise((r) => setTimeout(r, ms)));
  const total = esperas.length + 1;

  for (let intento = 0; intento < total; intento++) {
    try {
      return await fn();
    } catch (err) {
      // Un error del servidor es un veredicto: se propaga en el primer intento.
      if (!esFalloDeRed(err)) throw err;
      if (intento === total - 1) throw err;
      if (opciones.alReintentar) opciones.alReintentar(intento + 1, total, err);
      await dormir(esperas[intento]);
    }
  }
}

module.exports = { conReintentoDeRed, esFalloDeRed, ESPERAS_POR_DEFECTO };
